import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal } from '../../components';
import { modalClose } from '../../layout/redux/layoutActions';
import {
  getWhatsAppConnection,
  createWhatsAppChallenge,
  disconnectWhatsApp
} from './whatsappConnectionApi';
import texts from './whatsappConnectionTexts';
import './whatsappConnection.scss';

function getErrorText(error, t) {
  const status = error?.response?.status;

  if (status === 401) return t.unauthorized;
  if (status === 403) return t.forbidden;

  if (
    error?.code === 'ERR_NETWORK' ||
    error?.code === 'ECONNABORTED'
  ) {
    return t.network;
  }

  return t.generic;
}

function ConnectionModal({ language, onClose }) {
  const t = texts[language] || texts['pt-BR'];
  const locale = language === 'esp' ? 'es' : (
    language === 'en-US' ? 'en-US' : 'pt-BR'
  );

  const [connection, setConnection] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [busy, setBusy] = useState('initial');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [now, setNow] = useState(Date.now());

  const mounted = useRef(false);
  const operationRunning = useRef(false);
  const codeInput = useRef(null);

  const phone = (
    process.env.REACT_APP_YT_AGENT_WHATSAPP_NUMBER || ''
  ).trim();

  const validPhone = /^[1-9]\d{7,14}$/.test(phone);

  const expiresAt = challenge
    ? Date.parse(challenge.expiresAtUtc)
    : null;

  const expired = challenge ? now >= expiresAt : false;
  const connected = connection?.connected === true;

  const whatsappUrl = validPhone
    ? `https://wa.me/${phone}`
    : null;

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      setBusy('initial');
      setError('');

      try {
        const result = await getWhatsAppConnection(controller.signal);

        if (!controller.signal.aborted) {
          setConnection(result);
        }
      } catch (requestError) {
        if (!controller.signal.aborted) {
          setError(getErrorText(requestError, t));
        }
      } finally {
        if (!controller.signal.aborted) {
          setBusy('');
        }
      }
    }

    load();

    return () => controller.abort();
  }, [t]);

  useEffect(() => {
    if (!challenge) return undefined;

    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [challenge]);

  async function runOperation(operation) {
    if (operationRunning.current || busy) return;

    operationRunning.current = true;
    setBusy(operation);
    setError('');
    setNotice('');

    try {
      if (operation === 'create') {
        const result = await createWhatsAppChallenge();

        if (!mounted.current) return;

        setChallenge(result);
        setNow(Date.now());
      } else if (operation === 'disconnect') {
        await disconnectWhatsApp();

        if (!mounted.current) return;

        setConnection({
          connected: false,
          externalIdentifierMasked: null,
          verifiedAtUtc: null
        });
        setChallenge(null);
        setConfirmDisconnect(false);
        setNotice(t.disconnected);
      } else {
        const result = await getWhatsAppConnection();

        if (!mounted.current) return;

        setConnection(result);

        if (result.connected) {
          setChallenge(null);
        } else if (challenge) {
          setNotice(t.pending);
        }
      }
    } catch (requestError) {
      if (!mounted.current) return;

      // O vínculo pode ter sido concluído em outra aba
      // enquanto o usuário tentava gerar outro código.
      if (
        operation === 'create' &&
        requestError?.response?.status === 409 &&
        requestError?.response?.data?.errorCode === 'user_already_connected'
      ) {
        try {
          const result = await getWhatsAppConnection();

          if (!mounted.current) return;

          setConnection(result);

          if (result.connected) {
            setChallenge(null);
          } else {
            setError(t.generic);
          }
        } catch (statusError) {
          if (mounted.current) {
            setError(getErrorText(statusError, t));
          }
        }
      } else {
        setError(getErrorText(requestError, t));
      }
    } finally {
      operationRunning.current = false;

      if (mounted.current) {
        setBusy('');
      }
    }
  }

  async function copyCode() {
    if (!challenge || busy || operationRunning.current) return;

    if (Date.now() >= expiresAt) {
      setNow(Date.now());
      return;
    }

    operationRunning.current = true;
    setBusy('copy');
    setNotice('');
    setError('');

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }

      await navigator.clipboard.writeText(challenge.code);

      if (mounted.current) {
        setNotice(t.copied);
      }
    } catch {
      if (mounted.current) {
        codeInput.current?.focus();
        codeInput.current?.select();
        setNotice(t.copyFailed);
      }
    } finally {
      operationRunning.current = false;

      if (mounted.current) {
        setBusy('');
      }
    }
  }

  function validateSend(event) {
    if (busy || !challenge || Date.now() >= expiresAt) {
      event.preventDefault();
      setNow(Date.now());
    }
  }

  const sendUrl = whatsappUrl && challenge
    ? `${whatsappUrl}?text=${encodeURIComponent(challenge.code)}`
    : null;

  return (
    <Modal
      name='whatsapp-connection'
      title={t.title}
      size='medium'
      open={true}
      close={onClose}
      closeText={t.close}
      actions={
        <Button color='default' onClick={onClose}>
          {t.close}
        </Button>
      }
    >
      <div className='yt-whatsapp-connection'>
        {error && (
          <div className='yt-wa-message yt-wa-error' role='alert'>
            {error}
          </div>
        )}

                {notice && (
          <div
            className={`yt-wa-message${notice === t.pending ? ' yt-wa-error' : ''}`}
            role='status'
          >
            {notice}
          </div>
        )}

        {busy === 'initial' ? (
          <p role='status'>{t.loading}</p>
        ) : connection === null ? (
          <Button
            color='primary'
            disabled={!!busy}
            onClick={() => runOperation('status')}
          >
            {busy ? t.checking : t.retry}
          </Button>
        ) : connected ? (
          <>
            <div className='yt-wa-message yt-wa-success'>
              <strong>{t.connected}</strong>
              {connection.externalIdentifierMasked && (
                <div>{connection.externalIdentifierMasked}</div>
              )}
            </div>

            <p>{t.connectedHelp}</p>

            {!validPhone && (
              <p role='alert'>{t.invalidNumber}</p>
            )}

            {confirmDisconnect ? (
              <div className='yt-wa-confirm'>
                <p>{t.confirmDisconnect}</p>

                <div className='yt-wa-actions'>
                  <Button
                    color='default'
                    disabled={!!busy}
                    onClick={() => setConfirmDisconnect(false)}
                  >
                    {t.cancel}
                  </Button>

                  <Button
                    color='primary'
                    disabled={!!busy}
                    onClick={() => runOperation('disconnect')}
                  >
                    {busy === 'disconnect' ? t.disconnecting : t.confirm}
                  </Button>
                </div>
              </div>
            ) : (
              <div className='yt-wa-actions'>
                {whatsappUrl && (
                  <a
                    className='yt-wa-link'
                    href={whatsappUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {t.talk}
                  </a>
                )}

                <Button
                  color='default'
                  disabled={!!busy}
                  onClick={() => {
                    setError('');
                    setNotice('');
                    setConfirmDisconnect(true);
                  }}
                >
                  {t.disconnect}
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <p>{t.intro}</p>

            {!validPhone && (
              <p role='alert'>{t.invalidNumber}</p>
            )}

            {!challenge ? (
              <Button
                color='primary'
                disabled={!!busy || !validPhone}
                onClick={() => runOperation('create')}
              >
                {busy === 'create' ? t.generating : t.connect}
              </Button>
            ) : (
              <>
                <p>{t.instructions}</p>

                <label htmlFor='yt-wa-code'>{t.code}</label>

                <input
                  id='yt-wa-code'
                  className='yt-wa-code'
                  ref={codeInput}
                  value={challenge.code}
                  readOnly
                  spellCheck={false}
                  autoComplete='off'
                  onFocus={event => event.target.select()}
                />

                <p className='yt-wa-validity'>
                  {t.validity}: {new Date(expiresAt).toLocaleString(locale)}
                </p>

                {expired ? (
                  <div className='yt-wa-message' role='status'>
                    {t.expired}
                  </div>
                ) : (
                  <>
                    <div className='yt-wa-actions'>
                      <Button
                        color='default'
                        disabled={!!busy}
                        onClick={copyCode}
                      >
                        {t.copy}
                      </Button>

                      {sendUrl && (
                        <a
                          className={`yt-wa-link${busy ? ' is-disabled' : ''}`}
                          href={sendUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          aria-disabled={!!busy}
                          onClick={validateSend}
                        >
                          {t.send}
                        </a>
                      )}
                    </div>

                    <p className='yt-wa-help'>{t.sendHelp}</p>
                  </>
                )}

                <div className='yt-wa-actions'>
                  <Button
                    color='primary'
                    disabled={!!busy}
                    onClick={() => runOperation('status')}
                  >
                    {busy === 'status' ? t.checking : t.check}
                  </Button>

                  {expired && (
                    <Button
                      color='default'
                      disabled={!!busy}
                      onClick={() => runOperation('create')}
                    >
                      {busy === 'create' ? t.generating : t.regenerate}
                    </Button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

export default function WhatsAppConnection() {
  const dispatch = useDispatch();
  const { sistema, statusModal, usuario } = useSelector(
    state => state.layoutState
  );

  if (statusModal !== 'whatsappConnection') {
    return null;
  }

  return (
    <ConnectionModal
      key={usuario?.email || 'current-user'}
      language={sistema?.idioma || 'pt-BR'}
      onClose={() => dispatch(modalClose())}
    />
  );
}